import type { Dictionary } from './en'

/** Spanish. Typed against `Dictionary`, so a missing or extra key fails typecheck. */
export const es: Dictionary = {
  app: {
    name: 'Sastrex Intake',
    tagline: 'systematic mastery for the AI era',
    loading: 'Cargando…',
    skipToContent: 'Saltar al contenido',
  },
  nav: {
    groups: { files: 'Documentos', organization: 'Organización', admin: 'Administración Sastrex' },
    fileSync: 'Sincronización de archivos',
    workspaces: 'Espacios',
    adminOrgs: 'Organizaciones',
    adminFiles: 'Sincronización de archivos',
    adminProjects: 'Proyectos',
    help: 'Ayuda',
    orgSettings: 'Configuración de la organización',
    signOut: 'Cerrar sesión',
    openMenu: 'Abrir navegación',
    closeMenu: 'Cerrar navegación',
    theme: { label: 'Tema', system: 'Sistema', light: 'Claro', dark: 'Oscuro' },
    identity: '{{env}} · v{{version}}',
  },
  orgGate: {
    title: 'Elige una organización',
    body: 'Los documentos se comparten en nombre de una organización. Elige aquella para la que envías.',
    none: 'Aún no perteneces a ninguna organización. Pide una invitación a tu contacto en Sastrex y vuelve a iniciar sesión.',
  },
  config: {
    title: 'Este despliegue no está configurado',
    body: 'La aplicación no pudo leer su configuración. Si operas este despliegue, revisa los valores siguientes.',
    issues: 'Problemas encontrados',
  },
  errors: {
    generic: 'Algo salió mal. Inténtalo de nuevo.',
    reference: 'Referencia: {{id}}',
    retry: 'Reintentar',
    noAccessTitle: 'Aún no hay nada que hacer aquí',
    noAccessBody: 'Tu cuenta no tiene tareas en esta organización. Cambia de organización o pide acceso a tu administrador.',
    forbidden: 'No tienes acceso a esta página.',
    notFoundTitle: 'Página no encontrada',
    notFoundBody: 'La dirección puede estar mal escrita o la página se ha movido.',
    backHome: 'Ir al inicio',
    code: {
      network_error: 'No se pudo contactar con el servidor. Revisa tu conexión.',
      no_active_org: 'Selecciona primero una organización.',
      org_suspended: 'Esta organización está suspendida. Contacta con Sastrex.',
      insufficient_scope: 'A esta credencial le falta un permiso necesario.',
      rate_limited: 'Demasiadas solicitudes. Espera un momento e inténtalo de nuevo.',
      validation_failed: 'Algunos valores no son válidos.',
      invalid_token: 'Tu sesión expiró. Vuelve a iniciar sesión.',
      missing_token: 'Tu sesión expiró. Vuelve a iniciar sesión.',
      not_found: 'No encontrado.',
      forbidden: 'No tienes permiso para hacer esto.',
      internal_error: 'El servidor tuvo un error. Inténtalo de nuevo.',
      org_not_provisioned: 'Sastrex no configuró esta organización. Elige otra organización o contacta con Sastrex.',
      no_tenant_access: 'Aún no tienes asignado ningún inquilino. Pide al administrador de tu organización que te añada a uno.',
      tenant_required: 'Elige primero un inquilino.',
      tenant_not_active: 'Este inquilino no está activo. Elige otro.',
      platform_org_protected: 'La organización de Sastrex no se puede cambiar aquí.',
      provisioning_failed: 'No se pudo crear esta organización. Revisa los datos e inténtalo de nuevo.',
      tenant_archived: 'Este espacio está archivado. Sus documentos se conservan; pide a Sastrex que lo restaure.',
      tenant_limit_reached: 'Tu organización alcanzó el límite de espacios.',
      tenant_unavailable: 'El almacenamiento de este espacio no respondió. Inténtalo de nuevo en un momento.',
      admin_invite_failed: 'Se rechazó la invitación del administrador. Revisa el correo e inténtalo de nuevo.',
      idempotency_key_reused: 'Esta solicitud ya se envió con otros datos. Inténtalo de nuevo.',
    },
  },
  common: {
    cancel: 'Cancelar',
    close: 'Cerrar',
    copy: 'Copiar',
    copied: 'Copiado',
    loadMore: 'Cargar más',
    search: 'Buscar',
    all: 'Todos',
    revoke: 'Revocar',
    create: 'Crear',
    download: 'Descargar',
    never: 'Nunca',
    remove: 'Quitar',
    optional: 'Opcional',
  },
  status: {
    upload: {
      PENDING: 'Pendiente',
      UPLOADED: 'Recibido',
      REJECTED: 'Rechazado',
      EXPIRED: 'Expirado',
      DELETED: 'Eliminado',
    },
    channel: { WEB: 'Web', API: 'API', SFTP: 'SFTP' },
    org: { ACTIVE: 'Activa', SUSPENDED: 'Suspendida' },
    tenant: { PROVISIONING: 'Preparando', ACTIVE: 'Activo', FAILED: 'Falló la preparación', DEPROVISIONING: 'Archivando', ARCHIVED: 'Archivado' },
    item: {
      queued: 'Listo',
      hashing: 'Preparando',
      registering: 'Preparando',
      uploading: 'Subiendo',
      completing: 'Verificando',
      uploaded: 'Recibido',
      rejected: 'Rechazado',
      failed: 'Falló',
      cancelled: 'Cancelado',
      skipped: 'No enviado',
    },
  },
  fileSync: {
    title: 'Sincronización de archivos',
    subtitle: 'Envía documentos a Sastrex por subida, API o SFTP, y sigue cada archivo en un solo lugar.',
    tabsLabel: 'Formas de enviar archivos',
    tabs: { upload: 'Subir', api: 'API', sftp: 'SFTP' },
    files: 'Archivos',
    filesIntro: 'Todo lo recibido por cualquier canal, de todos los espacios que puedes ver. Selecciona un archivo para previsualizarlo.',
    search: 'Buscar por nombre de archivo',
    activeFilters: 'Filtros activos',
    removeFilter: 'Quitar el filtro {{column}}',
    clearAll: 'Quitar todos',
    columns: { file: 'Archivo', folder: 'Carpeta', batch: 'Lote', organization: 'Organización', workspace: 'Espacio', channel: 'Canal', status: 'Estado', fulfilment: 'Procesamiento', size: 'Tamaño', received: 'Recibido', by: 'Enviado por' },
    folderStartsWith: 'La carpeta empieza por',
    noBatches: 'Aún no hay lotes',
    batchesFailed: 'No se pudieron cargar los lotes.',
    batchFilterUnavailable: 'Elige una organización para filtrar por lote',
    bulk: {
      label: 'Archivos seleccionados',
      selectAll: 'Seleccionar todos los archivos visibles',
      select: 'Seleccionar {{name}}',
      selected_one: '{{formatted}} archivo seleccionado',
      selected_other: '{{formatted}} archivos seleccionados',
      progress: 'Descargando {{done}} de {{total}}…',
      download: 'Descargar como ZIP',
      clear: 'Quitar selección',
      max: 'Hasta {{count}} archivos a la vez.',
      saved_one: '{{count}} archivo guardado',
      saved_other: '{{count}} archivos guardados',
      skipped_one: '{{count}} archivo no se pudo incluir',
      skipped_other: '{{count}} archivos no se pudieron incluir',
      tooLarge: 'Demasiado grande para comprimir en este navegador (más de 1 GB). Usa Chrome o Edge, o selecciona menos archivos.',
      failed: 'La descarga se detuvo. Inténtalo de nuevo.',
      delete: 'Eliminar',
      deleting: 'Eliminando…',
      deleted_one: '{{count}} archivo eliminado',
      deleted_other: '{{count}} archivos eliminados',
      notDeleted_one: '{{formatted}} archivo no se pudo eliminar. Sigue seleccionado: inténtalo de nuevo o ábrelo para ver el motivo.',
      notDeleted_other: '{{formatted}} archivos no se pudieron eliminar. Siguen seleccionados: inténtalo de nuevo o ábrelos para ver el motivo.',
      deleteConfirm: {
        title_one: '¿Eliminar {{count}} archivo?',
        title_other: '¿Eliminar {{count}} archivos?',
        body: 'Se quitan del almacenamiento para toda tu organización. No se puede deshacer.',
        confirm: 'Eliminar',
      },
    },
    actions: {
      more: 'Más acciones',
      rename: 'Renombrar',
      delete: 'Eliminar',
      deleteConfirm: { title: '¿Eliminar «{{name}}»?', body: 'Se quita del almacenamiento para toda tu organización. No se puede deshacer.', confirm: 'Eliminar archivo' },
      deleteFailed: 'No se pudo eliminar el archivo: {{reason}}',
      remove: 'Quitar',
      removeConfirm: { title: '¿Quitar “{{name}}”?', body: 'Este archivo nunca llegó, así que no hay nada almacenado. Su entrada se quita de la lista para todos en tu organización.', confirm: 'Quitar entrada' },
    },
    rename: {
      title: 'Renombrar «{{name}}»',
      name: 'Nuevo nombre',
      hint: 'Cambia cómo se muestra y se descarga el archivo. El archivo almacenado no se toca.',
      submit: 'Renombrar',
      invalid: 'Usa de 1 a 255 caracteres, sin barras ni caracteres de control.',
      toast: 'Archivo renombrado',
    },
    admin: {
      title: 'Sincronización de archivos',
      subtitle: 'Elige un cliente y un espacio en la barra superior para encontrar sus archivos o enviarle archivos de las mismas formas que él.',
      organization: 'Organización',
      allOrganizations: 'Todas las organizaciones',
      orgSwitch: 'Organización cliente: {{name}}. Cambiar',
      pickOrg: 'Elige una organización para enviarle archivos, emitir claves API o configurar SFTP. Con todas las organizaciones puedes explorar, previsualizar y descargar.',
      suspended: 'Esta organización está suspendida: sus archivos se pueden ver y descargar, pero no se puede enviar nada.',
      acting: 'Estás actuando para {{org}}. Lo que envíes o configures lo verán ellos y quedará registrado con tu nombre.',
    },
    filter: { label: 'Filtrar por {{column}}', labelActive: 'Filtrar por {{column}} (activo)', apply: 'Aplicar', clear: 'Quitar' },
    groups: { documents: 'PDF', images: 'Imágenes', office: 'Documentos de Office', data: 'Datos y texto', archives: 'Archivos comprimidos' },
    any: 'Cualquiera',
    minMb: 'Mín. (MB)',
    maxMb: 'Máx. (MB)',
    from: 'Desde',
    to: 'Hasta',
    byContains: 'Nombre, correo o clave contiene',
    emptyTitle: 'Aún no hay archivos',
    emptyBody: 'Los archivos que envíes por subida, API o SFTP aparecen aquí.',
    noMatches: 'Ningún archivo coincide con estos filtros.',
    shown_one: '{{n}} archivo',
    shown_other: '{{n}} archivos',
    shownMore_one: '{{n}} archivo mostrado, hay más',
    shownMore_other: '{{n}} archivos mostrados, hay más',
    preview: {
      loading: 'Cargando…',
      close: 'Cerrar vista previa',
      of: 'Vista previa de {{name}}',
      csv: 'Contenido del archivo',
      notPreviewable: 'No hay vista previa para este tipo. Descárgalo para abrirlo.',
      notReceived: 'Solo se pueden previsualizar archivos recibidos.',
      missing: 'Este archivo no está disponible. Puede que se haya eliminado o que esté en un espacio que no puedes ver.',
      reason: 'Motivo',
      reference: 'Referencia',
    },
  },
  projects: {
    title: 'Proyectos',
    subtitle: 'Conjuntos seleccionados de documentos de un cliente, de cualquiera de sus espacios, listos para que un consumidor los obtenga. Los documentos se referencian, nunca se copian.',
    create: 'Nuevo proyecto',
    columns: { name: 'Proyecto', organization: 'Organización', documents: 'Documentos', status: 'Estado', created: 'Creado' },
    status: { ACTIVE: 'Activo', ARCHIVED: 'Archivado' },
    emptyTitle: 'Aún no hay proyectos',
    emptyBody: 'Crea un proyecto para una organización cliente y añade los documentos que necesita una prueba de concepto.',
    form: { title: 'Nuevo proyecto', organization: 'Organización cliente', chooseOrg: 'Elige una organización', name: 'Nombre', nameInvalid: 'Usa de 2 a 120 caracteres', description: 'Descripción', submit: 'Crear proyecto' },
    createdToast: 'Proyecto «{{name}}» creado',
    back: 'Todos los proyectos',
    documentsTitle: 'Documentos',
    documentsIntro: 'Cada documento permanece en el almacenamiento de su espacio. Solo los documentos aptos pueden procesarse en document_fullfilment.',
    eligible: 'Apto',
    notEligible: 'No apto',
    columnsDoc: { file: 'Archivo', workspace: 'Espacio', type: 'Tipo', size: 'Tamaño', fulfilment: 'Procesamiento', added: 'Añadido' },
    noDocuments: 'Aún no hay documentos. Añádelos desde los espacios de la organización.',
    add: 'Añadir documentos',
    remove: 'Quitar',
    removedToast: 'Quitado del proyecto',
    archive: 'Archivar proyecto',
    reactivate: 'Reactivar',
    archivedNotice: 'Este proyecto está archivado y no puede cambiar.',
    picker: { title: 'Añadir documentos a «{{name}}»', intro: 'Documentos recibidos de {{org}}, de todos sus espacios.', selected_one: '{{count}} seleccionado', selected_other: '{{count}} seleccionados', submit: 'Añadir al proyecto', inProject: 'Ya está en el proyecto', none: 'Ningún documento recibido coincide.' },
    addedToast_one: '{{count}} documento añadido',
    addedToast_other: '{{count}} documentos añadidos',
    skipped_one: '{{count}} documento no se añadió',
    skipped_other: '{{count}} documentos no se añadieron',
    keys: {
      title: 'Acceso de consumidores',
      intro: 'Las claves de proyecto permiten que el sistema de un desarrollador o un agente de IA lea este proyecto, y nada más, mediante la API de lectura o el servidor MCP. Son de solo lectura.',
      create: 'Crear clave de proyecto',
      createBody: 'La clave puede listar, describir y descargar los documentos de este proyecto. No puede cambiar nada.',
      nameHint: 'Quién o qué la usará, por ejemplo «puente de document_fullfilment».',
      nameInvalid: 'Usa de 2 a 80 caracteres',
      submit: 'Crear clave',
      createdToast: 'Clave de proyecto creada',
      secretTitle: 'Copia la clave de proyecto ahora',
      emptyTitle: 'No hay claves de proyecto',
      emptyBody: 'Crea una clave por consumidor, para poder revocar cada una por separado.',
      revokeTitle: '¿Revocar {{name}}?',
      revokeBody: 'El consumidor que usa esta clave pierde el acceso de inmediato. Los enlaces de descarga ya emitidos caducan en minutos.',
      revokeConfirm: 'Revocar clave',
      revokedToast: 'Clave de proyecto revocada',
      quickstartTitle: 'Guía rápida de la API de lectura y MCP',
      mcpBody: 'Los agentes de IA se conectan al servidor MCP con la misma clave. Herramientas: list_projects, list_documents, get_document, get_document_download_url, list_changes.',
      quickstartBody: 'Lista los documentos, sigue los cambios con el cursor y solicita un enlace de descarga temporal por documento. Cada lectura queda auditada.',
    },
  },
  workspaces: {
    switcher: { title: 'Espacio', label: 'Espacio: {{name}}. Cambiar', manage: 'Gestionar espacios' },
    title: 'Espacios',
    subtitle: 'Cada espacio guarda sus documentos físicamente separados de los demás: su propio almacenamiento, su propio acceso SFTP y sus propias claves.',
    create: 'Nuevo espacio',
    columns: { name: 'Espacio', status: 'Estado', members: 'Personas', sftp: 'Acceso SFTP' },
    default: 'Predeterminado',
    membersCount_one: '{{count}} persona',
    membersCount_other: '{{count}} personas',
    adminsSeeAll: 'Los administradores de la organización pueden usar todos los espacios.',
    noSftp: 'Sin configurar',
    actions: { members: 'Personas', rename: 'Renombrar', archive: 'Archivar', retry: 'Reintentar' },
    form: { title: 'Nuevo espacio', renameTitle: 'Renombrar espacio', name: 'Nombre', nameHint: 'Por ejemplo un departamento o un procesador, como «Finanzas UE».', nameInvalid: 'Usa de 2 a 80 caracteres', submit: 'Crear espacio', save: 'Guardar' },
    createdToast: 'El espacio «{{name}}» se está preparando',
    archiveConfirm: { title: '¿Archivar «{{name}}»?', body: 'Sus personas, claves y acceso SFTP dejan de funcionar. Sus documentos se conservan hasta que Sastrex los elimine.', confirm: 'Archivar espacio' },
    membersDialog: { title: 'Personas en «{{name}}»', body: 'Miembros de tu organización que pueden enviar a este espacio y verlo.', none: 'Aún no hay miembros que asignar. Invita personas desde Ajustes de la organización.', admin: 'Admin', save: 'Guardar personas', savedToast: 'Personas actualizadas' },
    picker: { label: 'Espacio', placeholder: 'Elige un espacio', hint: 'Los documentos van solo a este espacio.' },
    noAccess: { title: 'Aún no estás en ningún espacio', body: 'Pide a un administrador de tu organización que te añada a un espacio. Después podrás enviar documentos aquí.', checkAgain: 'Comprobar de nuevo' },
    setup: {
      title_one: 'Preparando tu espacio',
      title_other: 'Preparando {{count}} espacios',
      body: 'Podrás enviar documentos en cuanto esté listo. Esta página se actualiza sola.',
      slowTitle_one: 'Tu espacio está tardando más de lo normal',
      slowTitle_other: 'Tus espacios están tardando más de lo normal',
      slowBody: 'Sigue en preparación. Si no está listo en unos minutos, contacta a Sastrex.',
      failedTitle_one: 'No se pudo preparar tu espacio',
      failedTitle_other: 'No se pudieron preparar tus espacios',
      failedBody: 'Un administrador puede reintentarlo desde Espacios, o contacta a Sastrex.',
      elapsed: 'desde hace {{time}}',
      checked: 'Comprobado hace {{seconds}} s',
      checkingNow: 'Comprobando…',
    },
    column: 'Espacio',
    allWorkspaces: 'Todos los espacios',
  },
  upload: {
    title: 'Subir documentos',
    subtitle: 'Suelta archivos o carpetas completas. Cada archivo va directo al almacenamiento seguro y se verifica al llegar.',
    dropTitle: 'Suelta aquí archivos o carpetas',
    dropActive: 'Suelta para añadir',
    dropHint: 'PDF, imágenes, Word (.doc, .docx), Excel, PowerPoint, CSV, texto, JSON, XML o ZIP · hasta {{max}} por archivo',
    chooseFiles: 'Elegir archivos',
    chooseFolder: 'Elegir carpeta',
    unpackArchives: 'Descomprimir los ZIP en sus documentos',
    unpackHint: 'Cada documento dentro de un ZIP se envía por separado y se lista con el nombre del ZIP como carpeta. Desactívalo para enviar un ZIP como un solo archivo.',
    unpacking: 'Descomprimiendo {{name}}…',
    archive: {
      kept_whole: '{{name}} es demasiado grande para descomprimirlo aquí (más de {{max}}) y se enviará como un solo archivo.',
      too_large_to_send: '{{name}} supera {{max}} y no se puede enviar. Descomprímelo en tu equipo y suelta los documentos.',
      no_documents: '{{name}} no contiene documentos.',
      unreadable_drop: 'No se pudo leer {{name}}. Puede que se haya movido o eliminado desde que lo elegiste; suéltalo de nuevo.',
      added: '{{added}} documentos añadidos desde el ZIP, {{problems}} necesitan atención',
    },
    batchLabel: 'Nombre del lote',
    batchLabelHint: 'El equipo de Sastrex lo verá junto a estos archivos.',
    defaultLabel: 'Envío {{date}}',
    start_one: 'Subir {{formatted}} archivo',
    start_other: 'Subir {{formatted}} archivos',
    cancelAll: 'Cancelar todo',
    clear: 'Vaciar lista',
    newBatch: 'Iniciar un nuevo envío',
    cancelFile: 'Cancelar {{name}}',
    removeFile: 'Quitar {{name}}',
    queued_one: '{{formatted}} archivo listo · {{size}}',
    queued_other: '{{formatted}} archivos listos · {{size}}',
    progress: '{{percent}} de {{size}}',
    eligible: 'Listo para procesar',
    notEligible: 'Guardado, sin procesamiento automático',
    summary: {
      dismiss: 'Cerrar',
      title: 'Envío terminado',
      running: 'Subiendo · {{percent}}',
      finishedProblems_one: 'Terminado · {{count}} archivo requiere atención',
      finishedProblems_other: 'Terminado · {{count}} archivos requieren atención',
      nothingReceived: 'No se recibió nada',
      files: '{{done}} de {{total}} archivos',
      bytes: '{{done}} de {{total}}',
      eta: 'quedan unos {{time}}',
      etaUnknown: 'calculando el tiempo restante',
      filterLabel: 'Mostrar solo',
      retry_one: 'Reintentar {{count}} archivo',
      retry_other: 'Reintentar {{count}} archivos',
      download: 'Descargar resultados (CSV)',
      body: '{{uploaded}} recibidos · {{rejected}} rechazados · {{failed}} fallidos o cancelados · {{skipped}} no enviados',
      batchOpen: 'Este lote sigue abierto para que puedas reintentar los archivos que fallaron. Un nuevo envío crea un lote nuevo.',
      toast_one: '{{formatted}} archivo recibido',
      toast_other: '{{formatted}} archivos recibidos',
      viewHistory: 'Ver los archivos',
    },
    groups: {
      attention: 'Requieren atención',
      active: 'En curso',
      waiting: 'En espera',
      done: 'Recibidos',
      cancelled: 'Cancelados',
    },
    list: {
      label_one: '{{count}} archivo en este lote',
      label_other: '{{count}} archivos en este lote',
      showAll: 'Mostrar {{count}} más',
      showLess: 'Mostrar menos',
    },
    live: {
      progress: 'Envío al {{percent}}',
      finished: 'Envío terminado: {{uploaded}} recibidos, {{problems}} requieren atención',
    },
    results: {
      file: 'Archivo',
      size: 'Bytes',
      status: 'Estado',
      reason: 'Motivo',
      id: 'Id de subida',
    },
    leaveTitle: '¿Salir mientras se suben archivos?',
    leaveBody: 'Las subidas en curso se detendrán. Los archivos ya recibidos se conservan.',
    leaveConfirm: 'Salir y detener',
    leaveCancel: 'Quedarme',
    reason: {
      unsupported_file_type: 'Tipo de archivo no permitido',
      payload_too_large: 'El archivo supera el límite',
      empty_file: 'El archivo está vacío',
      name_too_long: 'El nombre del archivo es demasiado largo',
      system_file: 'Archivo del sistema, omitido',
      network_error: 'Se perdió la conexión',
      validation_failed: 'Rechazado por el servidor',
      size_mismatch: 'El tamaño no coincide',
      content_does_not_match_extension: 'El contenido no coincide con la extensión',
      extension_not_allowed: 'Tipo de archivo no permitido',
      checksum_mismatch: 'La suma de verificación no coincide',
      expired: 'El enlace de subida expiró',
      storage: 'El almacenamiento rechazó el archivo',
      rate_limited: 'Servidor ocupado; se desistió tras varios intentos',
      tenant_unavailable: 'El almacenamiento del espacio no respondió',
      invalid_state: 'Este lote está cerrado',
      org_suspended: 'Tu organización está suspendida',
      no_tenant_access: 'No perteneces a este espacio',
      request_failed: 'La solicitud no llegó',
      not_found: 'No existe o ya se eliminó',
      tenant_archived: 'Su espacio está archivado',
      internal_error: 'Error del servidor',
      nested_archive: 'ZIP dentro de un ZIP, omitido',
      encrypted_entry: 'Protegido con contraseña en el ZIP, omitido',
      unsupported_compression: 'La entrada del ZIP usa una compresión no admitida',
      archive_unreadable: 'No se pudo leer del ZIP (dañado)',
      other: 'No se pudo enviar',
    },
  },
  apiKeys: {
    title: 'Claves API',
    subtitle: 'Permite que tus sistemas envíen documentos directamente. Cada clave pertenece a esta organización.',
    create: 'Crear clave API',
    columns: { name: 'Nombre', key: 'Clave', scopes: 'Permisos', lastUsed: 'Último uso', created: 'Creada', status: 'Estado' },
    revoked: 'Revocada',
    active: 'Activa',
    emptyTitle: 'No hay claves API',
    emptyBody: 'Crea una clave para enviar documentos desde tus propios sistemas.',
    form: {
      title: 'Crear clave API',
      name: 'Nombre',
      namePlaceholder: 'Conector ERP',
      nameRequired: 'Escribe un nombre',
      scopes: 'Permisos',
      scopesRequired: 'Elige al menos un permiso',
      submit: 'Crear clave',
    },
    scope: { 'uploads:write': 'Enviar documentos', 'uploads:read': 'Consultar estado de envíos' },
    secret: {
      replayed: 'La clave ya se había creado, pero su secreto solo se muestra una vez. Revócala y crea una nueva.',
      title: 'Copia tu clave API ahora',
      body: 'Es la única vez que se muestra la clave completa. Guárdala en tu gestor de secretos.',
      done: 'Ya guardé la clave',
    },
    revokeTitle: '¿Revocar {{name}}?',
    revokeBody: 'Los sistemas que usan esta clave dejarán de funcionar de inmediato. No se puede deshacer.',
    revokeConfirm: 'Revocar clave',
    revokedToast: 'Clave API revocada',
    createdToast: 'Clave API creada',
    quickstart: { title: 'Inicio rápido', body: 'Registra el archivo, envía los bytes a la URL devuelta y luego márcalo como completo.' },
  },
  sftp: {
    title: 'SFTP',
    subtitle: 'Envía archivos desde tareas programadas o clientes de escritorio. El acceso usa solo claves SSH.',
    connection: 'Datos de conexión',
    host: 'Servidor',
    port: 'Puerto',
    username: 'Usuario',
    folder: 'Carpeta',
    add: 'Añadir clave SSH',
    columns: { label: 'Etiqueta', username: 'Usuario', fingerprint: 'Huella', lastLogin: 'Último acceso', created: 'Añadida' },
    emptyTitle: 'Aún no hay acceso SFTP',
    emptyBody: 'Añade una clave pública SSH para obtener un usuario de esta organización.',
    form: {
      title: 'Añadir clave SSH',
      label: 'Etiqueta',
      labelPlaceholder: 'Exportación nocturna',
      labelRequired: 'Escribe una etiqueta',
      publicKey: 'Clave pública',
      publicKeyHint: 'Pega el contenido del archivo .pub. Se aceptan ssh-ed25519, ecdsa o ssh-rsa de al menos 3072 bits.',
      publicKeyInvalid: 'Esto no parece una clave pública SSH',
      submit: 'Añadir clave',
    },
    revokeTitle: '¿Revocar el acceso SFTP de {{name}}?',
    revokeBody: 'El próximo acceso con esta clave será rechazado. Los archivos ya enviados se conservan.',
    revokeConfirm: 'Revocar acceso',
    revokedToast: 'Acceso SFTP revocado',
    createdToast: 'Clave SSH añadida',
    howTo: { title: 'Conéctate con tu cliente', cli: 'Línea de comandos', winscp: 'WinSCP', rclone: 'rclone', winscpBody: 'Nuevo sitio → SFTP → servidor, puerto y usuario de arriba → Avanzado → SSH → Autenticación → archivo de clave privada.' },
  },
  admin: {
    banner: 'Administración Sastrex: estás viendo todas las organizaciones cliente.',
    orgs: {
      title: 'Organizaciones cliente',
      subtitle: 'Crea un espacio para cada cliente e invita a su primer administrador.',
      create: 'Nueva organización',
      columns: { name: 'Organización', status: 'Estado', uploads: 'Envíos', volume: 'Volumen', lastUpload: 'Último envío', created: 'Creada' },
      emptyTitle: 'No hay organizaciones cliente',
      emptyBody: 'Crea una para invitar a un cliente.',
      suspend: 'Suspender',
      activate: 'Activar',
      suspendTitle: '¿Suspender {{name}}?',
      suspendBody: 'Se rechazarán miembros, claves API y accesos SFTP hasta que la actives de nuevo. Los archivos se conservan.',
      suspendConfirm: 'Suspender organización',
      statusToast: 'Organización actualizada',
      form: {
        title: 'Nueva organización cliente',
        name: 'Nombre de la organización',
        nameRequired: 'Escribe un nombre de 2 a 100 caracteres',
        adminEmail: 'Correo del administrador del cliente',
        adminEmailHint: 'Recibirá una invitación para unirse como administrador.',
        emailInvalid: 'Escribe un correo válido',
        submit: 'Crear e invitar',
      },
      createdToast: '{{name}} creada. Invitación enviada.',
    },
    orgDetail: {
      back: 'Todas las organizaciones cliente',
      sections: { workspaces: 'Espacios', people: 'Personas', documents: 'Documentos' },
      openFiles: 'Abrir en Sincronización de archivos',
      workspacesIntro: 'Cada espacio tiene su propio almacenamiento, acceso SFTP y claves. Los administradores del cliente también pueden gestionarlos.',
      peopleIntro: 'El cliente gestiona sus miembros en Ajustes de la organización. Aquí Sastrex puede invitar, reenviar y revocar invitaciones.',
      adminAccepted: 'El administrador del cliente se ha unido',
      adminPending: 'Esperando a que el administrador del cliente acepte',
      members: 'Miembros',
      invitations: 'Invitaciones pendientes',
      noMembers: 'Aún no se ha unido nadie.',
      noInvitations: 'No hay invitaciones pendientes.',
      role: { admin: 'Administrador', member: 'Miembro' },
      invite: 'Invitar a alguien',
      inviteForm: { title: 'Invitar a {{name}}', email: 'Correo', role: 'Rol', submit: 'Enviar invitación' },
      invitedToast: 'Invitación enviada',
      resend: 'Reenviar',
      resentToast: 'Invitación enviada de nuevo',
      revoke: 'Revocar',
      revokeConfirm: { title: '¿Revocar la invitación de {{email}}?', body: 'El enlace de su correo deja de funcionar.', confirm: 'Revocar invitación' },
      revokedToast: 'Invitación revocada',
      sent: 'Enviada {{date}}',
      joined: 'Se unió {{date}}',
      documentsIntro: 'Busca, previsualiza y descarga los archivos de esta organización, o envíale archivos, en Sincronización de archivos.',
    },
    uploads: {
      title: 'Todos los envíos',
      subtitle: 'Archivos recibidos de todas las organizaciones cliente.',
      orgFilter: 'Organización',
    },
  },
}
